/**
 * The LAN room itself: five tables, thirty machines, and a tuoppi beside every
 * one of them.
 *
 * The tables are hashed - they are what the run threads on the way to the
 * chair. The machines and the glasses on them are not.
 */

import {
  cylinder,
  decor,
  monitorScreen,
  solid,
  type CylinderSpec,
  type DecorSpec,
  type ScreenContent,
  type ScreenSpec,
  type SolidSpec,
} from '../world/Level';
import { PALJU } from './levelPalju';

/* ---------------------------------------------------- juhlasali tables -- */

/**
 * Five tables, six PCs each, running north-south against the back wall - the
 * way a LAN in a hall like this is actually set up. That leaves a 4.5 m runway
 * along the south side of the room, and the runway is the route.
 *
 * The aisles end at the north wall. The first sleeper provides a way onto
 * the tables, followed by two long gaps at the end of the chain. A fall into
 * either empty final aisle costs the trip back around the south table ends.
 *
 * The runway itself is two lines rather than one, and it is the baari that
 * decided which. The four bodies asleep in it (nukkuja-1 to -4) lie down the
 * *middle* of the 4.5 m, covering `z = -33.425 .. -31.975`, and what is left
 * is a lane on each side of them:
 *
 *   - the wall side, which is the side the sima is on. It starts at `-31.975`
 *     beside the first sleeper and is let out to `-32.575` by the fourth,
 *     because each man lies 0.2 m further north than the one before him. The
 *     counter is 0.75 m deep and the valvoja's bench 0.42 m, so the floor
 *     narrows to 1.23 m at the hanat and opens to 2.16 m at the bench - and
 *     that is the price and the payment of it. It is the only way to stand at
 *     a nozzle, and it is the one with furniture in it.
 *   - the table side, `z = -34.5` to the reef, which is 1.68 m of clean floor
 *     at the doorway end and 1.08 m by the last sleeper. It is the way the
 *     doorway points and it has nothing on it at all - and no way to buy a
 *     drink from, so a line that takes it has to come back across the reef at
 *     one of the gaps to pay the toll.
 *
 * The two therefore trade width across the room rather than running parallel:
 * the wall lane is bought tight and paid out wide, which is what lets a line
 * that has been held against the counter for the pour arrive at the corridor
 * mouth already pointing up it, and the table lane is the other way round.
 *
 * They used to lie against the two walls instead, which left a straight
 * 1.55 m band down the middle and made this the one stretch of the level with
 * a single answer. That was deliberate and it is what changed: with the glass
 * and the hanat both on the south wall, the man asleep in front of them was
 * asking the hall's only line to be run through a body at the exact moment it
 * has to stop. Moving the four inboard is what turns the errand into a choice
 * of side rather than a fight for one lane - and it is why the last fifteen
 * metres now read the same way as every other segment does (README, *Kaksi
 * linjaa joka pätkällä*).
 *
 * The x gaps between them are where the two lanes are joined: 1.1 m twice,
 * and one 0.2 m seam between the first two that is not a gap at all.
 */
// Keep the entry step and first transfer; the last two gaps require separate
// late take-offs. Extend the hall inside the barn's unused sauna footprint.
export const TABLE_CENTERS = [133.2, 136.0, 138.8, 143.0, 147.4];
export const LAN_HALL_EXTENSION = TABLE_CENTERS[4] - 144.4;
export const LAN_EAST_WALL = 147.5 + LAN_HALL_EXTENSION;

/**
 * The valkokangas on the end wall, per the floor plan - and the projector
 * nobody has switched off since last night, still running somebody's clip.
 *
 * The screen and the picture are two different rectangles on purpose. The
 * screen is 4.4 m of wall; the clip is 16:9, so it is fitted to the screen's
 * height and leaves a hand's width of white down either side. That is what a
 * projector aimed at a screen it does not quite match looks like, and it is
 * the state every hall like this one is in at nine in the morning.
 */
export const VALKOKANGAS = {
  /** The face the hall sees. The sheet itself hangs 25 cm behind it. */
  x: LAN_EAST_WALL - 0.3,
  minZ: -38.4,
  maxZ: -34.0,
  bottom: 0.8,
  height: 2.2,
} as const;

/**
 * The projector hangs in the final widened aisle, six metres from the screen.
 * Its 1.5:1 throw keeps it clear of the tables after the hall extension.
 */
export const PROJEKTORI = {
  /** The lens glass. The beam starts here, so the body is all behind it. */
  x: 144.2,
  y: 3.24,
  z: (VALKOKANGAS.minZ + VALKOKANGAS.maxZ) / 2,
  /** Barrel, then the body behind it, back along the throw. */
  barrel: 0.06,
  depth: 0.34,
  halfWidth: 0.18,
  halfHeight: 0.07,
} as const;
export const TABLE_HALF_WIDTH = 0.6;
// After a missed jump, the depleted momentum cannot buy a floor remount.
// The entry sleeper still provides a generous step onto the first tables.
const TABLE_HEIGHT = 0.85;
export const TABLE_Z: [number, number] = [-39.5, -34.5];

/** Kimmo's seat: east side of table 5, the farthest table from the door. */
export const KIMMO_TABLE_INDEX = 4;
export const KIMMO_SEAT_Z = -37.0;

/**
 * The five teams in the finaali, in table order. Kimmo's is the last one, on
 * the farthest table from the door - the same row his seat, his monitor and
 * the goal-coloured tabletop are on.
 *
 * Every row is signed with the team playing on it rather than with its own
 * number, because the number is what the venue needs and the name is what the
 * players use: nobody at a LAN says "table three", they say who is on it.
 * Kimmo's row is still the one you can pick out from the door - but that is
 * the yellow table and the yellow monitor doing it, not the wording.
 */
const KIMMO_TEAM_NAME = 'Team Mentality';

/**
 * Named the way Kimmo's is, because it is on two things rather than one: the
 * sign over pöytä 2 and the lippis three of the porukka standing out on the
 * floor are wearing. They are that team - it is their own table's aisle they
 * step back into once the way is clear - and a hat that said something the
 * room does not is a hat with a joke of its own. See `TAAS_PUPIS`.
 */
export const PUPIS_TEAM_NAME = 'Taas Pupis';

export const TEAM_NAMES: readonly string[] = [
  'Oriveden Kerberus',
  PUPIS_TEAM_NAME,
  'Osmolov Racing Team',
  'Team Kolme Jellonaa',
  KIMMO_TEAM_NAME,
];

/**
 * One size for all five, set by the longest name on the list.
 *
 * `createSign` draws the text at 52 % of the sign's *height* and does not
 * shrink it to fit, so it is the width that decides whether a name gets cut
 * off - see src/world/World.ts. Uniform across the row on purpose: five signs
 * at five different sizes read as five notices, and these are one scoreboard.
 */
export const TEAM_SIGN = { y: 2.6, z: -34.45, width: 2.4, height: 0.36 } as const;

export function buildTables(): SolidSpec[] {
  return TABLE_CENTERS.map((cx, i) =>
    solid(
      `poyta-${i + 1}`,
      i === KIMMO_TABLE_INDEX ? 'goal' : 'table',
      cx - TABLE_HALF_WIDTH,
      TABLE_Z[0],
      cx + TABLE_HALF_WIDTH,
      TABLE_Z[1],
      TABLE_HEIGHT,
    ),
  );
}

/**
 * What is up on each of the thirty machines, table by table.
 *
 * Six entries per table, in the order `buildComputers` walks them: the three
 * seats down the west side of that table from the back of the hall forwards,
 * then the three down the east side. Authored rather than rolled, because
 * which machine has what on it is a joke with a punchline and the punchlines
 * want placing: the browser and the desktops are the room's wallpaper, the two
 * mosaicked ones are found rather than shown - one in an aisle you would only
 * see by cutting between the tables, one facing you as you come in - and the
 * blue screen has been like that since before anybody fell asleep.
 *
 * Kimmo's own machine is the last entry of the last row, and it is the game.
 * It is the only screen in the hall you ever get close enough to read, because
 * sitting down at it is the end of the run.
 */
const SCREEN_CONTENT: readonly (readonly ScreenContent[])[] = [
  ['windows', 'browser', 'youtube', 'spotify', 'windows', 'game'],
  ['spotify', 'video', 'windows', 'browser', 'youtube', 'windows'],
  ['youtube', 'windows', 'bsod', 'game', 'spotify', 'browser'],
  ['browser', 'spotify', 'windows', 'youtube', 'windows', 'video'],
  ['windows', 'youtube', 'browser', 'spotify', 'game', 'bsod'],
];

/**
 * Six monitors per table: three down each long side.
 *
 * The picture goes on whichever face the chair is at - which is the same side
 * the keyboard is on, and is why both are decided by `side` here rather than
 * anywhere else. Get it wrong and half the hall is showing its desktop to the
 * back of somebody's monitor.
 */
function buildComputers(): { decor: DecorSpec[]; screens: ScreenSpec[] } {
  const items: DecorSpec[] = [];
  const screens: ScreenSpec[] = [];
  TABLE_CENTERS.forEach((cx, i) => {
    const seatZ = [-38.6, KIMMO_SEAT_Z, -35.4];
    for (const side of [-1, 1] as const) {
      const x0 = side < 0 ? cx - TABLE_HALF_WIDTH : cx + TABLE_HALF_WIDTH - 0.3;
      seatZ.forEach((cz, seat) => {
        const isKimmo = i === KIMMO_TABLE_INDEX && side > 0 && seat === 1;
        const id = `${i + 1}-${side > 0 ? 'i' : 'l'}${seat + 1}`;
        const height = isKimmo ? 0.62 : 0.55;
        items.push(
          decor(
            `naytto-${id}`,
            isKimmo ? 'goal' : 'monitor',
            x0,
            cz - 0.45,
            x0 + 0.3,
            cz + 0.45,
            height,
            TABLE_HEIGHT,
          ),
        );
        screens.push(
          monitorScreen(
            `kuva-${id}`,
            SCREEN_CONTENT[i][side < 0 ? seat : 3 + seat],
            // The face the chair is at: the box's west side for a west seat,
            // its east side for an east one.
            side < 0 ? x0 : x0 + 0.3,
            cz,
            0.9,
            TABLE_HEIGHT,
            height,
            side,
          ),
        );
        // Keyboard, so the seats read as seats.
        items.push(
          decor(
            `nappis-${id}`,
            'metal',
            side < 0 ? cx - TABLE_HALF_WIDTH - 0.42 : cx + TABLE_HALF_WIDTH + 0.12,
            cz - 0.22,
            side < 0 ? cx - TABLE_HALF_WIDTH - 0.12 : cx + TABLE_HALF_WIDTH + 0.42,
            cz + 0.22,
            0.04,
            TABLE_HEIGHT,
          ),
        );
      });
    }
  });
  return { decor: items, screens };
}

/** Built once: `decorItems` takes the boxes and `screens` takes the pictures. */
export const COMPUTERS = buildComputers();

/* ------------------------------------------------------- tuopit pöydillä -- */

/**
 * The dimensions every tuoppi in the level is drawn to, at the bar and on the
 * tables alike. See `tuoppi()` for why a glass is a stack and not a container.
 *
 * Exported for the one glass in the game that is not a level cylinder: the one
 * in Hannu's hand, which `src/render/Figure.ts` hangs off a moving arm and
 * therefore has to build itself. It is still this glass.
 */
export const TUOPPI = {
  /** The beer, and the walls it stands inside. */
  radius: 0.042,
  wall: 0.044,
  /** The rim. A pour nobody has touched carries its head above this. */
  height: 0.14,
  foam: 0.02,
  /** What the last mouthful leaves in the bottom of a finished one. */
  dregs: 0.008,
} as const;

/**
 * One tuoppi, drawn as whatever is left in it.
 *
 * Nothing in the blockout is transparent - every material is an opaque Lambert
 * surface - so a glass cannot be a container with beer inside it. It is a
 * stack instead: beer from the base up to the level somebody drank it down to,
 * the collar on top of that, and the empty part of the glass above. Which is
 * all you read from across a room anyway - the level in the glass, never the
 * glass.
 *
 * `fill` is 1 for a pour that was set down and forgotten and 0 for one that
 * was finished. At 1 the head stands proud of the rim and there is no glass
 * left to draw, which is exactly the two-part tuoppi the bar has always had.
 *
 * Every part is named `<name>-kalja|-vaahto|-pohja|-lasi` and nothing is named
 * `<name>` on its own, so which parts a glass got is a question about that
 * glass rather than about how the level happens to be assembled.
 */
export function tuoppi(
  name: string,
  x: number,
  z: number,
  y: number,
  fill: number,
  /**
   * How much wider than a tuoppi this one is drawn, 1 being a tuoppi.
   *
   * Only the stacks on the tuoppipöytä use it, and it is the whole of what
   * makes them read as stacks. Nested glasses are all one material and all one
   * radius, so a pile of them drawn straight is a single seamless pale column
   * - a tall glass, not four short ones. Real glassware is tapered and a real
   * stack therefore steps outwards on the way up, which is the silhouette the
   * eye actually knows this object by. See `TUOPPIPOYTA.flare`.
   */
  spread = 1,
): CylinderSpec[] {
  const parts: CylinderSpec[] = [];
  const radius = TUOPPI.radius * spread;
  const wall = TUOPPI.wall * spread;
  let level = y;

  if (fill > 0) {
    const beer = TUOPPI.height * fill;
    parts.push(
      cylinder(`${name}-kalja`, 'beer', x, z, radius, beer, y, { radialSegments: 10 }),
      // The head, drawn as its own cap rather than as a lighter top face: a
      // CylinderGeometry has one colour all the way up.
      cylinder(`${name}-vaahto`, 'porcelain', x, z, wall, TUOPPI.foam, y + beer, {
        radialSegments: 10,
      }),
    );
    level = y + beer + TUOPPI.foam;
  } else {
    // An empty glass is never quite empty, and the ring in the bottom is the
    // whole difference between one that was drunk and one nobody ever poured.
    parts.push(
      cylinder(`${name}-pohja`, 'spill', x, z, radius, TUOPPI.dregs, y, {
        radialSegments: 10,
      }),
    );
    level = y + TUOPPI.dregs;
  }

  if (level < y + TUOPPI.height) {
    parts.push(
      cylinder(`${name}-lasi`, 'glass', x, z, wall, y + TUOPPI.height - level, level, {
        radialSegments: 10,
      }),
    );
  }
  return parts;
}

/**
 * What is standing on the tables: `[dx, z, fill]` per row, `dx` measured from
 * that row's centre line.
 *
 * Authored against the table and not against a seat, because a table is where
 * they end up. The 60 cm down the middle of a row is the only clear surface in
 * the hall - the monitors have the outer thirds and the keyboards hang off the
 * edges - so that strip is where everybody's glass goes, and by nine in the
 * morning whose glass is whose has stopped being a question anybody can
 * answer. The three out on the ends are beside a monitor rather than in the
 * strip, which is where the fourth one goes once the middle is full.
 *
 * More of them are empty than are not, for the same reason the hall is asleep.
 *
 * The last entry is the one that matters: it is beside Kimmo's own monitor,
 * and it is finished, which is the short version of how he ended up in the
 * palju.
 *
 * Pöytä 2 has its middle glass back. It stood empty at `[-0.18, -35.3, 0]`
 * until the tuoppi the porukka sends Kimmo for was authored 40 cm along the
 * same table, at which point one of the two was a takeable object with an
 * identical twin beside it that was not - and telling them apart was a
 * question about which one the HUD lit up rather than about anything in the
 * room. The takeable one stands on the baari's own side table now, see
 * `TUOPPIPOYTA`, so this row is dressing again like every other row here.
 */
const TUOPIT_POYDILLA: readonly (readonly (readonly [number, number, number])[])[] = [
  [
    [-0.2, -38.8, 0],
    [0.17, -37.85, 0.55],
    [0.45, -36.2, 0],
  ],
  [
    [-0.24, -39.2, 1],
    [0.12, -37.1, 0.35],
    [-0.18, -35.3, 0],
  ],
  [
    [0.21, -38.4, 0],
    [-0.13, -36.75, 0.85],
    [0.05, -35.05, 0],
  ],
  [
    [-0.45, -37.9, 0.6],
    [0.18, -36.9, 0],
    [-0.08, -35.4, 0.45],
  ],
  [
    [0.2, -38.7, 0],
    [-0.22, -36.1, 1],
    [0.45, -36.3, 0],
  ],
];

export const POYTIEN_TUOPIT: CylinderSpec[] = TUOPIT_POYDILLA.flatMap((row, i) =>
  row.flatMap(([dx, z, fill], j) =>
    tuoppi(`lanituoppi-${i + 1}-${j + 1}`, TABLE_CENTERS[i] + dx, z, TABLE_HEIGHT, fill),
  ),
);

/* --------------------------------------------------- tuopit paljun reunalla - */

/**
 * What is standing on the palju's rim.
 *
 * This is out of route order - the palju is half an hour up the level from the
 * juhlasali - and it is here because `tuoppi()` is here. There is one glass in
 * this level and it is drawn one way, at the bar, on the tables and out in the
 * yard alike; a second copy of that stack authored next to `PALJU` is how the
 * empties in the yard end up a different object from the empties indoors.
 *
 * They are the reason the tub is worth looking at from either side. Both ways
 * past the palju run within arm's length of the rim - the 2.25 m portti along
 * the south side, the klapiteline corridor along the north - and until now the
 * only thing on it was the rack. A rim with somebody's tuoppi on it says the
 * night ended here rather than that a tub was placed here.
 *
 * `[degrees, dr, fill]`, going anticlockwise from due east, `dr` measured off
 * the middle of the 35 cm ring so no two of them stand at the same distance
 * out. Two angles are spoken for and neither is authored into:
 *
 *   0 deg +/- 15   the kamiina's meno- and paluuputki come over the rim there,
 *                  low enough that a glass would stand inside one
 *   ~180 deg       nothing, but it is the far side from both lines and a glass
 *                  nobody can see is a glass nobody drew
 *
 * Mostly finished, one or two still with something in them, same as the hall:
 * see `TUOPIT_POYDILLA` for why a ring of full pints reads as a table setting
 * rather than as a night.
 */
const PALJUN_TUOPIT_SPEC: readonly (readonly [number, number, number])[] = [
  [118, 0.03, 0.45],
  [152, -0.05, 0],
  [196, 0.04, 0],
  [225, -0.02, 0.7],
  [252, 0.05, 0],
  [274, -0.04, 0.25],
  [298, 0.02, 1],
  [331, -0.03, 0],
];

/** The middle of the rim ring: the staves at `radius`, the outside at +0.35. */
const PALJUN_REUNA_R = PALJU.radius + 0.175;

export const PALJUN_TUOPIT: CylinderSpec[] = PALJUN_TUOPIT_SPEC.flatMap(
  ([degrees, dr, fill], i) => {
    const angle = (degrees / 180) * Math.PI;
    const r = PALJUN_REUNA_R + dr;
    return tuoppi(
      `paljutuoppi-${i + 1}`,
      PALJU.x + Math.cos(angle) * r,
      PALJU.z + Math.sin(angle) * r,
      PALJU.rim,
      fill,
    );
  },
);
